import { ISelectOption } from '@/components/form/fields/SelectField';
import { axiosInstance } from '@/utils/libs/axios';
import { useMutation, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { IDetailTestRunSchema } from './testRunDetail';
import { IShareTestRunSchema } from './testRunShare';
import { IReportSchema } from './release';

interface IReleaseItem {
  id: string;
  name: string;
}

interface IUserItem {
  id: string;
  full_name: string;
}

interface ITestPlanItem {
  id: string;
  plan_name: string;
}

export const testRunSchema = z.object({
  id: z.string().optional(),
  title: z.string().max(255).min(1),
  version: z.string().max(255).optional().nullable(),
  browser: z.string().max(255).optional().nullable(),
  description: z.string().max(255).optional().nullable(),
  assignee_id: z.string(),
  release_id: z.string(),
  project_id: z.string().optional(),
  test_plan_id: z.string().nullish(),
  test_case_ids: z.array(z.string()).optional(),
  module_ids: z.array(z.string()).optional(),
});

export type ITestRunSchema = z.infer<typeof testRunSchema> & {
  details?: IDetailTestRunSchema[];
  report?: IReportSchema;
};

export type IReleaseAndAssignee = {
  release: ISelectOption[];
  assign: ISelectOption[];
};

export type ITestPlanOption = {
  testPlan: ISelectOption[];
};

export const TestRunService = {
  useGetList: (projectId?: string) =>
    useQuery<{ id: string; title: string }[]>({
      queryKey: ['test-run-list', projectId],
      queryFn: async () => {
        if (!projectId) return [];

        const response = await axiosInstance.get('/api/crud/test-run', {
          params: {
            filter: { project_id: projectId, is_archive: 'false' },
            show_all: true,
          },
        });

        const rawData =
          response.data?.data?.content ??
          response.data?.data ??
          response.data ??
          [];
        return Array.isArray(rawData) ? rawData : [];
      },
      enabled: !!projectId,
    }),

  useGetReleaseAndAssignee: (projectId?: string) =>
    useQuery<IReleaseAndAssignee>({
      queryKey: ['release-and-assignee'],
      queryFn: async () => {
        const releaseResponse = await axiosInstance.get('/api/crud/release', {
          params: { filter: { project_id: projectId } },
        });

        const releaseOptions = releaseResponse.data.data.content.map(
          (item: IReleaseItem) => ({
            value: item.id,
            label: item.name,
          }),
        );

        const userResponse = await axiosInstance.get('/api/crud/project-assignee/all', {
          params: { filter: { project_id: projectId } },
        });

        const userOptions = userResponse.data.all.filter((item: IUserItem) => item.full_name?.trim() && item.id).map((item: IUserItem) => ({
          value: item.id,
          label: item.full_name,
        }));

        return {
          release: releaseOptions ?? [],
          assign: userOptions ?? [],
        };
      },
    }),

  useGetTestPlan: (projectId?: string) => {
    return useQuery<ITestPlanOption>({
      queryKey: ['test-plan'],
      queryFn: async () => {
        const response = await axiosInstance.get('/api/crud/test-plan', {
          params: { filter: { project_id: projectId } },
        });

        const testPlanOptions = response.data.data.content.map(
          (item: ITestPlanItem) => ({
            value: item.id,
            label: item.plan_name,
          }),
        );

        return {
          testPlan: testPlanOptions ?? [],
        };
      },
    });
  },

  useGet: (id?: string) =>
    useQuery<ITestRunSchema>({
      queryKey: ['test-run', id],
      queryFn: async () => {
        if (!id) return {};

        const response = await axiosInstance.get(`/api/crud/test-run/${id}`);

        return response.data?.data;
      },
    }),

  useCreate: () =>
    useMutation({
      mutationFn: async (data: ITestRunSchema) => {
        const response = await axiosInstance.post('/api/crud/test-run', data);

        return response.data;
      },
    }),

  useUpdate: () =>
    useMutation({
      mutationFn: async (data: ITestRunSchema) => {
        const response = await axiosInstance.post(
          `/api/crud/test-run/${data.id}`,
          data,
        );

        return response.data;
      },
    }),

  useDelete: () =>
    useMutation({
      mutationFn: async (id: string) => {
        const response = await axiosInstance.delete(`/api/crud/test-run/${id}`);

        return response.data;
      },
    }),

  useArchive: () =>
    useMutation({
      mutationFn: async (id: string) => {
        const response = await axiosInstance.post(
          `/api/crud/test-run/${id}/archive`,
        );

        return response.data;
      },
    }),

  useUnarchive: () =>
    useMutation({
      mutationFn: async (id: string) => {
        const response = await axiosInstance.post(
          `/api/crud/test-run/${id}/unarchive`,
        );

        return response.data;
      },
    }),

  useGetShare: (id?: string) =>
    useQuery<IShareTestRunSchema>({
      queryKey: ['test-run-shared', id],
      queryFn: async () => {
        if (!id) return {};

        const response = await axiosInstance.get(
          `/api/crud/test-run/${id}/share`,
        );

        return response.data?.data;
      },
    }),
};
